import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ToastCard, ToastViewport } from '@quizzach/styles';
import type { ToastSeverity } from '@quizzach/styles';
import {
	IcAlertRound,
	IcAlertTriangle,
	IcCheck,
	IcClose,
	IcInfo,
} from '@quizzach/assets';

const TOAST_DURATION_MS = 4200;

const severityIcon = {
	error: IcAlertRound,
	warning: IcAlertTriangle,
	info: IcInfo,
} as const;

interface ToastOptions {
	message: string;
	severity?: ToastSeverity;
	actionLabel?: string;
	onAction?: () => void;
}

interface ToastEntry extends ToastOptions {
	id: number;
	/* bumped when an identical toast is re-requested; keys the duration
	   meter so its shrink animation restarts */
	renewals: number;
}

interface ToastContextValue {
	showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue>({
	showToast: () => undefined,
});

export const useToast = () => useContext(ToastContext);

/**
 * Non-blocking toast stack: bottom-right on desktop, above the bottom nav
 * on mobile. Pill cards with a severity icon, an auto-dismiss meter and a
 * manual close; an optional action (e.g. "zaključi kupnju" or "pokušaj
 * ponovno") dismisses on tap.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	const [toasts, setToasts] = useState<ToastEntry[]>([]);
	// source of truth lives in refs so timer side effects stay out of state
	// updaters (StrictMode double-invokes those); state only mirrors for render
	const toastsRef = useRef<ToastEntry[]>([]);
	const timersRef = useRef(new Map<number, number>());
	const idRef = useRef(0);

	const commit = useCallback(() => {
		setToasts([...toastsRef.current]);
	}, []);

	const dismiss = useCallback(
		(id: number) => {
			const timer = timersRef.current.get(id);
			if (timer !== undefined) {
				window.clearTimeout(timer);
				timersRef.current.delete(id);
			}
			toastsRef.current = toastsRef.current.filter((toast) => toast.id !== id);
			commit();
		},
		[commit]
	);

	const showToast = useCallback(
		(options: ToastOptions) => {
			const severity = options.severity ?? 'info';
			const existing = toastsRef.current.find(
				(toast) =>
					toast.message === options.message &&
					(toast.severity ?? 'info') === severity
			);

			// an identical toast is already up (e.g. repeated failing clicks):
			// restart its timer and meter instead of stacking a duplicate
			if (existing) {
				const timer = timersRef.current.get(existing.id);
				if (timer !== undefined) window.clearTimeout(timer);
				timersRef.current.set(
					existing.id,
					window.setTimeout(() => dismiss(existing.id), TOAST_DURATION_MS)
				);
				toastsRef.current = toastsRef.current.map((toast) =>
					toast.id === existing.id
						? { ...toast, ...options, renewals: toast.renewals + 1 }
						: toast
				);
				commit();
				return;
			}

			idRef.current += 1;
			const id = idRef.current;
			toastsRef.current = [
				...toastsRef.current,
				{ ...options, id, renewals: 0 },
			];
			timersRef.current.set(
				id,
				window.setTimeout(() => dismiss(id), TOAST_DURATION_MS)
			);
			commit();
		},
		[commit, dismiss]
	);

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			{createPortal(
				<ToastViewport aria-live='polite'>
					<AnimatePresence>
						{toasts.map((toast) => {
							const severity = toast.severity ?? 'info';
							const Icon =
								severity === 'success' ? IcCheck : severityIcon[severity];

							return (
								<ToastCard
									key={toast.id}
									$severity={severity}
									$durationMs={TOAST_DURATION_MS}
									as={motion.div}
									initial={{ opacity: 0, y: 16, scale: 0.97 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: 8, scale: 0.97 }}
									transition={{ duration: 0.24, ease: [0.2, 0.65, 0.2, 1] }}
									layout
								>
									<span
										className={severity === 'success' ? 't-ic t-ok' : 't-ic'}
									>
										<Icon size={severity === 'success' ? 14 : 19} />
									</span>
									<span className='t-msg'>{toast.message}</span>
									{toast.actionLabel && (
										<button
											type='button'
											className='t-action'
											onClick={() => {
												toast.onAction?.();
												dismiss(toast.id);
											}}
										>
											{toast.actionLabel}
										</button>
									)}
									<button
										type='button'
										className='t-x'
										aria-label={t('common.close')}
										onClick={() => dismiss(toast.id)}
									>
										<IcClose size={13} />
									</button>
									<span className='t-dur' key={`dur-${toast.renewals}`} />
								</ToastCard>
							);
						})}
					</AnimatePresence>
				</ToastViewport>,
				document.body
			)}
		</ToastContext.Provider>
	);
};
